<template>
  <page-list
    show-tag-filter
    :list="list"
    :columns="columns"
    :group-actions="groupActions"
    :single-actions="singleActions"
    :extTagParams="extTagParams"
    :tagManagerInstance="tagManagerInstance"
    tagFilterResource="meter_instances"
    :tagBtnText="$t('dictionary.instance_tag')"
    :export-data-options="exportDataOptions" />
</template>

<script>
import * as R from 'ramda'
import WindowsMixin from '@/mixins/windows'
import ListMixin from '@/mixins/list'
import {
  getNameFilter,
  getBrandDistinctFilter,
  getAccountFilter,
  getDomainFilter,
  getRegionFilter,
} from '@/utils/common/tableFilter'
import { getBillResourceTypeLabel } from '@Bill/utils'
import ColumnsMixin from '../mixins/columns'

export default {
  name: 'ResourceTagList',
  mixins: [WindowsMixin, ListMixin, ColumnsMixin],
  props: {
    id: String,
    getParams: {
      type: Object,
      default: () => ({}),
    },
  },
  data () {
    const emptyValue = this.$t('common.empty_value')
    const showFields = ['name', 'resource_type', 'origin_res_type', 'metadata', 'brand']

    return {
      list: this.$list.createList(this, {
        id: this.id,
        apiVersion: 'v1',
        resource: 'meter_instance_caches',
        getParams: this.getParam,
        filterOptions: {
          external_id: {
            label: this.$t('bill.resource_tag.external_id'),
            filter: true,
            formatter: val => {
              const realVal = val.map(item => `'${item}'`)
              return `external_id.in(${realVal})`
            },
          },
          id: {
            label: this.$t('table.title.id'),
          },
          name: getNameFilter(),
          brand: getBrandDistinctFilter(),
          projects: {
            label: this.$t('dictionary.project'),
            dropdown: true,
            multiple: true,
            distinctField: {
              type: 'field',
              key: 'project',
            },
          },
          project_domain: getDomainFilter('domain', 'tenant', 'field'),
          account: getAccountFilter({ label: this.$t('res.cloudaccount'), distinctType: 'field', field: 'account', hiddenField: 'account' }),
          region: getRegionFilter({ distinctType: 'field', withEmptyValue: true }),
          resource_type: {
            label: this.$t('bill.resource_tag.resource_type'),
            dropdown: true,
            multiple: true,
            distinctField: {
              type: 'field',
              key: 'resource_type',
              afterFetch: async (items) => {
                return items.map(item => {
                  return {
                    key: item === '' ? emptyValue : item,
                    label: item === '' ? emptyValue : getBillResourceTypeLabel(item),
                  }
                })
              },
            },
            filter: true,
            formatter: val => {
              console.log(val)
              if (val.length === 1 && val[0] === emptyValue) {
                return 'resource_type.isnullorempty()'
              }
              const newItems = val.map(item => {
                if (item === emptyValue) {
                  return '\' \''
                }
                return `'${item}'`
              })
              return `resource_type.in(${newItems.join(',')})`
            },
          },
          origin_res_type: {
            label: this.$t('bill.resource_tag.origin_resource_type'),
            dropdown: true,
            multiple: true,
            distinctField: {
              type: 'field',
              key: 'origin_res_type',
              afterFetch: async (items) => {
                return items.map(item => {
                  return {
                    key: item === '' ? emptyValue : item,
                    label: item === '' ? emptyValue : item,
                  }
                })
              },
            },
            filter: true,
            formatter: val => {
              if (val.length === 1 && val[0] === emptyValue) {
                return 'origin_res_type.isnullorempty()'
              }
              const newItems = val.map(item => {
                if (item === emptyValue) {
                  return '\' \''
                }
                return `'${item}'`
              })
              return `origin_res_type.in(${newItems.join(',')})`
            },
          },
        },
      }),
      singleActions: [
        {
          label: this.$t('compute.perform_change_owner', [this.$t('dictionary.project')]),
          permission: 'meter_instances_perform_change_owner',
          action: (obj) => {
            this.createDialog('ChangeOwenrDialog', {
              data: [obj],
              custom_columns: this.columns.filter(item => showFields.includes(item.field)),
              manager: new this.$Manager('meter_instances'),
              name: this.$t('bill.resource_tag'),
              resource: 'meter_instances',
              steadyStatus: null,
              ignoreCandidateDomains: true,
              refresh: this.refresh,
            })
          },
          meta: (obj) => {
            const ret = {
              validate: true,
              tooltip: null,
            }
            if (!this.$store.getters.isAdminMode) {
              ret.validate = false
              return ret
            }
            return ret
          },
        },
      ],
      groupActions: [
        // 编辑标签
        {
          label: this.$t('compute.text_283'),
          permission: 'meter_instances_perform_set_user_metadata',
          action: () => {
            this.createDialog('SetTagDialog', {
              data: this.list.selectedItems,
              columns: this.columns,
              manager: new this.$Manager('meter_instances', 'v2'),
              refresh: this.refresh,
              onManager: this.onManager,
              params: {
                resources: 'meter_instance',
              },
              mode: 'add',
            })
          },
          meta: () => {
            const ret = {
              validate: this.list.selectedItems.length,
            }
            if (!this.$store.getters.isAdminMode) {
              ret.validate = false
              return ret
            }
            return ret
          },
        },
        {
          label: this.$t('network.text_225', [this.$t('dictionary.project')]),
          action: () => {
            this.createDialog('ChangeOwenrDialog', {
              data: this.list.selectedItems,
              custom_columns: this.columns.filter(item => showFields.includes(item.field)),
              manager: new this.$Manager('meter_instances'),
              name: this.$t('bill.resource_tag'),
              resource: 'meter_instances',
              steadyStatus: null,
              ignoreCandidateDomains: true,
              refresh: this.refresh,
            })
          },
          meta: () => {
            const ret = {
              validate: this.list.selectedItems.length,
            }
            if (!this.$store.getters.isAdminMode) {
              ret.validate = false
              return ret
            }
            return ret
          },
        },
      ],
    }
  },
  computed: {
    exportDataOptions () {
      const ret = {
        downloadType: 'local',
        title: this.$t('bill.resource_tag'),
        items: [
          { label: 'ID', key: 'id' },
          ...this.columns,
        ],
      }
      this.columns.map(col => {
        if (col.field === 'project') {
          ret.items.push({ field: 'domain', title: this.$t('dictionary.domain') })
        }
        if (col.field === 'region') {
          ret.items.push({ field: 'zone', title: this.$t('dictionary.zone') })
        }
        if (col.field === 'account') {
          ret.items.push({
            field: 'spec.manager',
            title: this.$t('dictionary.cloudprovider'),
            formatter: ({ row }) => {
              return row.spec?.manager || '-'
            },
          })
        }
      })
      return ret
    },
    extTagParams () {
      return {
        scope: this.$store.getters.scope,
        with_user_meta: true,
        with_cloud_meta: false,
        resources: 'meter_instance',
      }
    },
  },
  created () {
    this.tagManagerInstance = new this.$Manager('metadatas/tag-value-pairs', 'v2')
    this.fetchData()
  },
  methods: {
    handleOpenSidepage (row) {
      this.sidePageTriggerHandle(this, 'ResourceTagSidePage', {
        id: row.id,
        resource: 'meter_instance_caches',
        getParams: this.getParam,
      }, {
        list: this.list,
      })
    },
    fetchData () {
      this.list.fetchData()
    },
    refresh () {
      this.list.fetchData()
    },
    getParam () {
      const getParams = (R.is(Function, this.getParams) ? this.getParams() : this.getParams)
      const ret = {
        ...getParams,
      }
      return ret
    },
  },
}
</script>

<style>
</style>
